/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useRef, useEffect } from 'react';
import { 
  TextField, 
  IconButton, 
  Typography, 
  Box,
  useTheme,
  Tooltip,
} from '@mui/material';
import { 
  Send, 
  Chat, 
  Close,
  ContentCopy
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context/ThemeContext';
import { ChatMessageInterface } from 'medicalsystem-shared';
import { formatTimeForChat } from 'utils/date.utils';

interface VideochatChatProps {
  isOpen: boolean;
  onClose: () => void;
  messages: ChatMessageInterface[];
  onSendMessage: (message: string) => void;
  currentUser: string;
  disabled?: boolean;
  showCloseButton?: boolean;
}

const TextMessagesChat: React.FC<VideochatChatProps> = ({
  isOpen,
  onClose,
  messages,
  onSendMessage,
  currentUser,
  disabled = false,
  showCloseButton = true
}) => {
  const { t } = useTranslation();
  const { darkMode } = useMaterialUIController();
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const isDark = controller.darkMode;
  const [newMessage, setNewMessage] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const handleChatMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage.trim() || disabled) return;
    
    onSendMessage(newMessage.trim());
    setNewMessage('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyChat = async () => {
    if (messages.length === 0) {
      return;
    }

    const chatText = messages.map((msg) => {
      const time = formatTimeForChat(msg.timestamp);
      return `[${msg.sender}] (${time}) - ${msg.message}`;
    }).join('\n');

    try {
      await navigator.clipboard.writeText(chatText);
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
      console.log('Chat copiado al portapapeles');
    } catch (err) {
      console.error('Error al copiar chat:', err);
      const textArea = document.createElement('textarea');
      textArea.value = chatText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  return (
    <Box
      onClick={handleChatClick}
      onMouseDown={handleChatMouseDown}
      className={`text-messages-chat ${isDark ? 'dark' : ''}`}
    >
      <Box
       id="cy-chat-header"
       className={`chat-header ${isDark ? 'dark' : ''}`}
      >
        <Box className="chat-header-content" >
          <Chat className="chat-icon" />
          <Typography variant="h6" className='chat-title'>
            {t('videochat.chat')}
          </Typography>
          
          <IconButton
            onClick={handleCopyChat}
            title={t('videochat.copyChat')}
            className="icon-button"
          >
            <ContentCopy className="content-copy-icon" />
          </IconButton>
          
          {copySuccess && (
            <Typography 
              variant="caption" 
              className="copy-success-message"
            >
              {t('videochat.chatCopied')}
            </Typography>
          )}
        </Box>
        
        {showCloseButton && (
          <Tooltip
            title={t('videochat.closeChat')}
            placement="top"
            arrow
            enterDelay={500}
            leaveDelay={200}
            PopperProps={{
              className: 'tooltip'
            }}
          >
            <IconButton
              onClick={onClose}
              className="close-button"
            >
              <Close className="content-copy-icon" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        id="cy-chat-messages"
        ref={chatMessagesRef}
        className={`chat-messages ${isDark ? 'dark' : ''}`}
      >
        {messages.length === 0 ? (
          <Box className={`empty-messages ${isDark ? 'dark' : ''}`}>
            <Chat className={`empty-messages-icon ${isDark ? 'dark' : ''}`} />
            <Typography sx={{ color: 'inherit' }}>
              {t('videochat.noMessages') || 'No hay mensajes aún'}
            </Typography>
            <Typography className={`empty-messages-small-text ${isDark ? 'dark' : ''}`}>
              {t('videochat.startConversation') || 'Inicia la conversación'}
            </Typography>
          </Box>
        ) : (
          messages.map((msg) => (
            <Box
              key={msg.id}
              className={`message-container ${msg.isOwn ? 'own' : 'other'}`}
            >
              <Box
                className={`message-bubble ${msg.isOwn ? 'own' : 'other'}`}
              >
                {msg.message}
              </Box>
              
              <Box className={`message-metadata ${isDark ? 'dark' : ''}`}>
                <span>{msg.sender}</span>
                <span>•</span>
                <span>
                  {formatTimeForChat(msg.timestamp)}
                </span>
              </Box>
            </Box>
          ))
        )}
      </Box>
      <Box 
        id="cy-chat-input"
        className={`chat-input ${isDark ? 'dark' : ''}`}
      >
        <Box className="input-wrapper">
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={3}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('videochat.typeMessage')}
            disabled={disabled}
            variant="outlined"
            size="small"
            className={`chat-text-field ${isDark ? 'dark' : ''}`}
          />
          <Tooltip
            title={t('videochat.sendMessage')} 
            placement="top"
            arrow
            enterDelay={500}
            leaveDelay={200}
            PopperProps={{
              className: 'tooltip'
            }}
          >
            <span style={{ display: 'inline-block' }}>
              <IconButton
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || disabled}
                className={`send-button ${isDark ? 'dark' : ''} ${disabled ? 'disabled' : ''}`}
              >
                <Send className="send-icon" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography 
          variant="caption" 
          className={`chat-caption ${isDark ? 'dark' : ''}`}
        >
          {t('videochat.pressEnterToSend')}
        </Typography>
      </Box>
    </Box>
  );
};

export default TextMessagesChat;